import React, { forwardRef, useEffect, useRef } from "react";

/**
 * Seamless looping background video.
 *
 * The native `loop` attribute re-seeks/re-decodes from frame 0 at the boundary,
 * which produces a brief freeze + a visible jump (the last frame rarely matches
 * the first). To avoid this we stack two identical <video> elements and, a short
 * window before the active clip ends, start a fresh-from-zero copy on the other
 * element and crossfade between them, then swap roles. The incoming clip is
 * already decoded in its own element, so there is no reload stall, and the
 * opacity blend masks the end/start frame mismatch.
 *
 * Only `opacity` is animated (CSS transition) — no layout/transform work.
 * Respects prefers-reduced-motion: leaves a single static poster frame and does
 * not autoplay.
 *
 * Props:
 *  - src, poster: forwarded to the <video> elements
 *  - className: applied to the wrapper (the ref is forwarded here, e.g. for GSAP)
 *  - videoClassName: applied to both <video> elements
 */

// When (seconds before end) to begin the crossfade, and how long it lasts.
// FADE is kept comfortably shorter than START_BEFORE_END so the blend always
// finishes before the outgoing clip reaches its end, even given the coarse
// (~250ms) granularity of the `timeupdate` event.
const START_BEFORE_END = 0.9;
const FADE = 0.6;

const SeamlessLoopVideo = forwardRef(function SeamlessLoopVideo(
  { src, poster, className = "", videoClassName = "" },
  ref
) {
  const aRef = useRef(null);
  const bRef = useRef(null);

  useEffect(() => {
    const a = aRef.current;
    const b = bRef.current;
    if (!a || !b) return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReduced) {
      a.style.opacity = "1"; // show the static poster, no playback
      return;
    }

    let active = a; // the clip currently in front
    let swapping = false;
    let endTimer = 0;
    const safePlay = (v) => {
      const p = v.play();
      if (p && p.catch) p.catch(() => {});
    };

    const finishSwap = (outgoing) => {
      if (endTimer) {
        clearTimeout(endTimer);
        endTimer = 0;
      }
      outgoing.removeEventListener("ended", onEnded);
      outgoing.pause();
      outgoing.currentTime = 0;
      swapping = false;
    };

    function onEnded() {
      // `this` is the outgoing clip that just played out behind the new one.
      finishSwap(this);
    }

    const onTimeUpdate = (e) => {
      const cur = e.target;
      if (cur !== active || swapping) return;
      const dur = cur.duration;
      if (!dur || cur.currentTime < dur - START_BEFORE_END) return;

      swapping = true;
      const outgoing = cur;
      const incoming = outgoing === a ? b : a;

      incoming.currentTime = 0;
      safePlay(incoming);
      incoming.style.opacity = "1";
      outgoing.style.opacity = "0";
      active = incoming;

      // Reset the outgoing clip once it finishes playing out behind the blend.
      outgoing.addEventListener("ended", onEnded);
      // Safety net in case `ended` doesn't fire (e.g. backgrounded tab).
      endTimer = window.setTimeout(
        () => finishSwap(outgoing),
        (START_BEFORE_END + FADE) * 1000
      );
    };

    a.addEventListener("timeupdate", onTimeUpdate);
    b.addEventListener("timeupdate", onTimeUpdate);

    // Warm up the second decoder so the first crossfade is as smooth as later
    // ones — but ONLY after the front clip is actually playing. Warming it at
    // mount means two copies of the (multi-MB) file download + decode at once on
    // first paint, and that contention is what makes the hero video stutter on
    // load. The second element starts at preload="none" (see JSX) so it doesn't
    // fetch until then; there's ample lead time before the first crossfade,
    // which only fires ~START_BEFORE_END seconds before the clip ends.
    let warmed = false;
    let warmTimer = 0;
    const warmSecond = () => {
      if (warmed) return;
      warmed = true;
      if (warmTimer) {
        clearTimeout(warmTimer);
        warmTimer = 0;
      }
      b.preload = "auto";
      const warm = b.play();
      if (warm && warm.then) {
        warm
          .then(() => {
            b.pause();
            b.currentTime = 0;
          })
          .catch(() => {});
      }
    };
    // Warm shortly after the front clip stabilizes; fall back to a timer in case
    // `playing` never fires (e.g. tab opened in the background).
    const onActivePlaying = () => {
      warmTimer = window.setTimeout(warmSecond, 600);
    };
    a.addEventListener("playing", onActivePlaying, { once: true });
    const warmFallback = window.setTimeout(warmSecond, 2500);

    // Reveal the front clip only once it actually has a frame to show. Until
    // then it stays transparent so the section's poster background shows through
    // instead of an empty gray <video> box — that box is the gray flash on cold
    // load. (The poster attribute alone doesn't cover it: the element renders
    // gray until the poster image itself has decoded.)
    const revealActive = () => {
      a.style.opacity = "1";
    };
    if (a.readyState >= 2) revealActive();
    else a.addEventListener("loadeddata", revealActive, { once: true });

    // Start the front clip.
    b.style.opacity = "0";
    safePlay(a);

    return () => {
      a.removeEventListener("timeupdate", onTimeUpdate);
      b.removeEventListener("timeupdate", onTimeUpdate);
      a.removeEventListener("loadeddata", revealActive);
      a.removeEventListener("playing", onActivePlaying);
      a.removeEventListener("ended", onEnded);
      b.removeEventListener("ended", onEnded);
      if (endTimer) clearTimeout(endTimer);
      if (warmTimer) clearTimeout(warmTimer);
      clearTimeout(warmFallback);
    };
  }, [src]);

  return (
    <div ref={ref} className={className}>
      <video
        ref={aRef}
        src={src}
        muted
        playsInline
        preload="auto"
        poster={poster}
        disablePictureInPicture
        disableRemotePlayback
        className={videoClassName}
        style={{ opacity: 0, transition: `opacity ${FADE}s linear` }}
      />
      <video
        ref={bRef}
        src={src}
        muted
        playsInline
        preload="none"
        disablePictureInPicture
        disableRemotePlayback
        className={videoClassName}
        style={{ opacity: 0, transition: `opacity ${FADE}s linear` }}
      />
    </div>
  );
});

export default SeamlessLoopVideo;
